// User Interview List ****************************************************************************
// pathname: /v1/interview/user-interview-list
// ************************************************************************************************
import {
  useSwrInfiniteApi,
} from '@hooks/useSwrApi';
import { ApiSchema } from '@type/api/apiUtilityFn';
import { GenericV1ErrorsArray } from '@type/v1-api-types/_global';

// Interface
// *****************************************************
export interface UseSwrUserInterviewListProps {
  locale: string;
  serverSideData?: ApiSchema<'InterviewListResult'> | GenericV1ErrorsArray;
  jobSlug?: string | null;
  userSlug?: string | null;
  urlParams?: string;
  shouldFetch?: boolean;
}

/**
 * @description SWR: fetch user interview list with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiSchema<'InterviewListResult'> | GenericV1ErrorsArray, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **jobSlug**: string | null, optional. If provided, filters interviews for a specific job. For CV detail page with applicants, pass the current job slug.
 * * **userSlug**: string | null, optional. If provided, fetches interviews for a specific user. For CV detail page, pass the user's slug.
 * * **urlParams**: string, optional. Additional URL parameters. Default empty string.
 * * **shouldFetch**: boolean, optional. Only fire the request when true. The endpoint requires authentication, so pass the auth state here. Default true.
 */
export const useSwrUserInterviewList = (props: UseSwrUserInterviewListProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    serverSideData,
    jobSlug,
    userSlug,
    urlParams = '',
    shouldFetch = true,
  } = props;

  // Build URL params with jobSlug and userSlug if provided
  const jobSlugParam = jobSlug ? `&jobSlug=${encodeURIComponent(jobSlug)}` : '';
  const userSlugParam = userSlug ? `&userSlug=${encodeURIComponent(userSlug)}` : '';
  const fullUrlParams = `${jobSlugParam}${userSlugParam}${urlParams}`;

  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'InterviewListResult'>>({
    url: '/v1/interview/user-interview-list',
    urlParams: fullUrlParams,
    shouldFetch,
    locale,
    serverSideData,
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60, // 1 minute
    },
    toastsOptions: {
      showErrorToast: true,
    },
    infiniteConfig: {
      paginationType: 'page',
      limit: 10,
      responseArrayKey: 'items',
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  };
};
